import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css, keyframes } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router';
import { BirdiTheme } from '@birdi/theme';
import { Spinner } from '@birdi/components-common';
import CloseSVG from '@birdi/icons/close.svg';
import SearchSVG from '@birdi/icons/search.svg';
import {
  auth,
  organisation as organisationAPI,
  search as searchAPI,
  stroot,
  GlobalSearchResult,
} from '@birdi/js-sdk';
import type { MemberWorkspaceSummary } from '@birdi/js-sdk/src/public/organisation';
import { useGlobalSearch, ViewportBbox } from './use-global-search';
import { SEARCH_PLACEHOLDER } from './search-copy';
import type { GlobePin } from './pin-collapse';
import { ResultsList, layoutTiles } from './results-list';
import { useMapDatasets } from './use-map-datasets';
import { WorkspaceFilter } from './workspace-filter';

// Cesium only loads when the overlay opens — keep it out of the entry bundle.
const SearchGlobe = React.lazy(() => import('./search-globe'));

interface AnchorRect {
  left: number;
  width: number;
  top: number;
  height: number;
  /** The field's corner radius, so the panel grows out of its real shape. */
  radius: number;
}

export interface GlobalSearchOverlayProps {
  onClose: () => void;
  /**
   * The search pill that opened the overlay. The panel starts at its exact
   * rect and grows to full screen, so the pill appears to become the modal.
   */
  anchorEl?: HTMLElement | null;
  /**
   * PRD rule: opened from a map or mission, the viewport filter starts as
   * that map/mission's extent and the globe opens looking at it.
   */
  initialBbox?: ViewportBbox | null;
  /**
   * PRD rule: the map search was opened from is always first on the initial
   * list, even when the area filter cannot match it (no location).
   */
  initialMapId?: string | null;
}

export const GlobalSearchOverlay = ({
  onClose,
  anchorEl,
  initialBbox = null,
  initialMapId = null,
}: GlobalSearchOverlayProps) => {
  const navigate = useNavigate();

  // Open animation: mount the panel at the pill's rect, then on the next
  // frame switch to the full-screen rect and let CSS transition the
  // position, size and corner radius. Reduced-motion users (and opens with
  // no pill to grow from) jump straight to the expanded state.
  const [origin] = useState<AnchorRect | null>(() => {
    if (!anchorEl) return null;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return null;
    }
    const r = anchorEl.getBoundingClientRect();
    const radius = parseFloat(
      window.getComputedStyle(anchorEl).borderTopLeftRadius,
    );
    return {
      left: r.left,
      width: r.width,
      top: r.top,
      height: r.height,
      radius: Number.isFinite(radius) ? radius : PANEL_RADIUS,
    };
  });
  const [expanded, setExpanded] = useState(origin === null);
  useLayoutEffect(() => {
    if (expanded) return undefined;
    const frame = requestAnimationFrame(() => setExpanded(true));
    return () => cancelAnimationFrame(frame);
  }, [expanded]);
  const panelStyle: React.CSSProperties =
    origin && !expanded
      ? {
          top: origin.top,
          left: origin.left,
          width: origin.width,
          height: origin.height,
          borderRadius: origin.radius,
        }
      : {
          top: '1rem',
          left: '1rem',
          width: 'calc(100vw - 2rem)',
          height: 'calc(100vh - 2rem)',
          borderRadius: PANEL_RADIUS,
        };
  const {
    query,
    setQuery,
    viewportBbox,
    setViewportBbox,
    workspaceId,
    setWorkspaceId,
    byType,
    bounds,
    loadMore,
    pins,
    loading,
  } = useGlobalSearch(initialBbox, initialMapId);
  const paging = useMemo(
    () => ({
      map: { ...byType.map, onLoadMore: () => loadMore('map') },
      mission: { ...byType.mission, onLoadMore: () => loadMore('mission') },
      dataset: { ...byType.dataset, onLoadMore: () => loadMore('dataset') },
    }),
    [byType, loadMore],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selected, setSelected] = useState<GlobalSearchResult | null>(null);

  // The input is sized to its text (via a hidden mirror) so the caret can
  // sit at the start of the centred placeholder.
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState<number>(2);
  useLayoutEffect(() => {
    const width = mirrorRef.current?.offsetWidth ?? 0;
    setInputWidth(Math.max(2, width + 2));
  }, [query]);

  // PRD: while a map result is selected, its datasets show in their own
  // "Selected Map Datasets" tile alongside the normal Datasets tile.
  const selectedMap = selected?.type === 'map' ? selected : null;
  const { datasets: mapDatasets, loading: mapDatasetsLoading } = useMapDatasets(
    selectedMap?.id ?? null,
  );

  // Tiles + the flat list in the same order, so the keyboard index follows
  // what is on screen.
  const { ordered, tiles } = useMemo(
    () =>
      layoutTiles(
        {
          map: byType.map.results,
          mission: byType.mission.results,
          dataset: byType.dataset.results,
        },
        mapDatasets,
        selectedMap ? selectedMap.name : null,
      ),
    [byType, mapDatasets, selectedMap],
  );

  // One request feeds the filter and goTo; goTo awaits it so a fast click
  // cannot navigate before the list arrives.
  const [workspacesRequest] = useState(() =>
    organisationAPI
      .getMyWorkspaces(stroot('gsrch'), { includeMenuIcon: true })
      .then((res) => res.body?.items ?? [])
      .catch((): MemberWorkspaceSummary[] => []),
  );
  const [workspaces, setWorkspaces] = useState<MemberWorkspaceSummary[]>([]);
  useEffect(() => {
    let cancelled = false;
    workspacesRequest.then((items) => {
      if (!cancelled) setWorkspaces(items);
    });
    return () => {
      cancelled = true;
    };
  }, [workspacesRequest]);

  const goTo = useCallback(
    (path: string, workspaceId: string) => {
      workspacesRequest.then((items) => {
        const mine = items.filter((ws) => ws.id === workspaceId);
        // The map app loads under the session scope and 403s for any other
        // workspace, so switch first, then do a full load to refresh meta.
        if (mine.length === 0 || mine.some((ws) => ws.active)) {
          onClose();
          navigate(path);
          return;
        }
        auth.switchContext(stroot('gsrch'), { scopeId: mine[0].scopeId }).then(
          () => window.location.assign(path),
          () => alert('Could not switch to that workspace. Please try again.'),
        );
      });
    },
    [navigate, onClose, workspacesRequest],
  );

  // Explicit navigation — the go-to-map icon, the button over the globe,
  // or activating an already-selected row.
  const goToMap = useCallback(
    (result: GlobalSearchResult) => {
      let path: string | null = `/map/next/${result.id}`;
      if (result.type === 'mission') path = `/map/missions/${result.id}`;
      else if (result.type === 'dataset')
        path = result.mapId ? `/map/next/${result.mapId}` : null;
      if (path) goTo(path, result.workspaceId);
    },
    [goTo],
  );

  // Clicking / entering a row selects it and zooms the globe to it;
  // activating it again navigates into its map.
  const activate = useCallback(
    (result: GlobalSearchResult) => {
      if (selected?.id === result.id) goToMap(result);
      else setSelected(result);
    },
    [goToMap, selected],
  );

  // Zoom-to icon on a row: select it so the globe flies there, no navigation.
  const zoomTo = useCallback((result: GlobalSearchResult) => {
    setSelected(result);
  }, []);

  const download = useCallback((result: GlobalSearchResult) => {
    searchAPI
      .requestDownload(stroot('gsrch'), result.type, result.id)
      .then(() => {
        // Matches the map app's existing zip download UX (layers/base.tsx).
        alert(
          'An email will be sent to your account containing a link for a direct download of the files selected.',
        );
      })
      .catch(() => {
        alert('No downloadable files were found for this item.');
      });
  }, []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (ordered.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((i) => Math.min(ordered.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const result = ordered[selectedIndex];
      if (result) activate(result);
    }
  };

  // PRD rule: typing a new term is a fresh, global search — it drops any
  // viewport filter left over from panning the globe. Deleting the term
  // (empty value) keeps it, so the list falls back to browsing the area.
  const onQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim()) setViewportBbox(null);
    setSelectedIndex(0);
    setSelected(null);
  };

  const hasQuery = query.trim().length > 0;
  // Browsing the current viewport: hold the camera where the user put it.
  const lockCamera = !hasQuery && viewportBbox !== null;

  // PRD globe rule: a map pin opens the map directly.
  const openMapPin = useCallback(
    (pin: GlobePin) => goTo(`/map/next/${pin.id}`, pin.workspaceId),
    [goTo],
  );

  // Clicking a mission/dataset pin filters the search to that item AND selects it, so
  // the camera flies to the clicked pin — not to the envelope of everything
  // its name happens to fuzzy-match.
  const onPinClick = useCallback(
    (pin: GlobePin) => {
      setQuery(pin.name);
      setSelectedIndex(0);
      setSelected({
        type: pin.type,
        id: pin.id,
        name: pin.name,
        workspaceId: pin.workspaceId,
        workspaceName: null,
        mapId: null,
        layerType: null,
        thumbnailUrl: null,
        createdAt: '',
        sizeBytes: null,
        center: pin.center,
        bbox: null,
        score: 1,
      });
    },
    [setQuery],
  );

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay css={overlayCss} />
        <Dialog.Content
          css={contentCss}
          style={panelStyle}
          data-expanded={expanded}
          onKeyDown={onKeyDown}
          data-testid="global-search-overlay"
          aria-describedby={undefined}
        >
          <Dialog.Title css={visuallyHiddenCss}>Global search</Dialog.Title>
          {/* The header IS the pill: same left padding/icon/font as the
              closed pill, close cross where the ⌘K chip was — so nothing
              shifts on expand. */}
          <header css={headerCss}>
            <SearchSVG />
            {/* Content-sized input + sibling placeholder, centred as a pair,
                so the caret sits at the START of the placeholder text
                rather than mid-field (as a centred native input would). */}
            <div css={inputWrapCss} onClick={() => inputRef.current?.focus()}>
              <span ref={mirrorRef} css={mirrorCss} aria-hidden>
                {query}
              </span>
              <input
                ref={inputRef}
                autoFocus
                type="text"
                aria-label={SEARCH_PLACEHOLDER}
                value={query}
                style={{ width: inputWidth }}
                onChange={(event) => onQueryChange(event.target.value)}
                data-testid="global-search-input"
              />
              {!query && <span css={placeholderCss}>{SEARCH_PLACEHOLDER}</span>}
            </div>
            <Dialog.Close asChild>
              <button type="button" css={closeCss} aria-label="Close search">
                <CloseSVG />
              </button>
            </Dialog.Close>
          </header>
          <div css={bodyCss}>
            <div css={globeCss}>
              {/* PRD: a spinner over the globe while any list request is in
                  flight, so a slow search never looks like an empty one. */}
              {loading && (
                <div css={globeLoadingCss} data-testid="global-search-loading">
                  <Spinner />
                </div>
              )}
              <Suspense
                fallback={
                  <div css={globeFallbackCss}>
                    <Spinner />
                  </div>
                }
              >
                <SearchGlobe
                  pins={pins}
                  results={ordered}
                  bounds={bounds}
                  selected={selected}
                  onPinClick={onPinClick}
                  onPinOpen={openMapPin}
                  onViewportChange={setViewportBbox}
                  lockCamera={lockCamera}
                  initialView={initialBbox}
                  browsing={!hasQuery}
                />
              </Suspense>
            </div>
            <aside css={listPaneCss}>
              <WorkspaceFilter
                workspaces={workspaces}
                value={workspaceId}
                onChange={(id) => {
                  setWorkspaceId(id);
                  setSelectedIndex(0);
                  setSelected(null);
                }}
              />
              <ResultsList
                tiles={tiles}
                ordered={ordered}
                mapDatasetsLoading={mapDatasetsLoading}
                selectedIndex={selectedIndex}
                loading={loading}
                query={query}
                areaFiltered={viewportBbox !== null}
                paging={paging}
                onHighlight={setSelectedIndex}
                onActivate={activate}
                onZoomTo={zoomTo}
                onDownload={download}
                onGoToMap={goToMap}
              />
            </aside>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/** Matches the rest of the UI (cards, buttons); the pill itself stays round. */
const PANEL_RADIUS = 5;
/* Above Intercom (its launcher/banner sit at ~2147483000). */
const Z_SCRIM = 2147483600;
const Z_PANEL = 2147483601;

const OPEN_MS = 260;
const OPEN_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const scrimIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const overlayCss = css`
  position: fixed;
  inset: 0;
  background: rgba(60, 60, 60, 0.45);
  z-index: ${Z_SCRIM};
  animation: ${scrimIn} ${OPEN_MS}ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Position, size and radius come from `panelStyle` (pill rect → full
   screen with a 1rem gap); this transitions between the two. The body fades
   in once the panel has grown so text never squashes mid-flight. */
const contentCss = (theme: BirdiTheme) => css`
  position: fixed;
  z-index: ${Z_PANEL};
  display: flex;
  flex-direction: column;
  background: ${theme.sand};
  /* Same border as the nav pill so its outline continues as the panel's. */
  border: 1px solid ${theme.midGrey};
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  transition:
    top ${OPEN_MS}ms ${OPEN_EASING},
    left ${OPEN_MS}ms ${OPEN_EASING},
    width ${OPEN_MS}ms ${OPEN_EASING},
    height ${OPEN_MS}ms ${OPEN_EASING},
    border-radius ${OPEN_MS}ms ${OPEN_EASING};

  &:focus {
    outline: none;
  }

  &[data-expanded='false'] > *:not(header) {
    opacity: 0;
  }
  &[data-expanded='true'] > *:not(header) {
    opacity: 1;
    transition: opacity 160ms ease-out ${Math.round(OPEN_MS * 0.5)}ms;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &[data-expanded='true'] > *:not(header) {
      transition: none;
    }
  }
`;

const headerCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 44px;
  flex-shrink: 0;
  padding: 0 0.5rem 0 1rem;
  background: ${theme.mono0};
  border-bottom: 1px solid ${theme.dove};
  color: ${theme.mono70};

  > svg {
    flex-shrink: 0;
  }

  input {
    min-width: 2px;
    max-width: 100%;
    padding: 0;
    border: none;
    outline: none;
    background: none;
    font-size: 1.125rem;
    color: inherit;
  }
`;

/* Fills the pill; centres the [input][placeholder] pair. */
const inputWrapCss = css`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: text;
  overflow: hidden;
`;

/* Off-screen twin of the input text, used only for measuring width. */
const mirrorCss = css`
  position: absolute;
  visibility: hidden;
  white-space: pre;
  font-size: 1.125rem;
  pointer-events: none;
`;

const placeholderCss = (theme: BirdiTheme) => css`
  font-size: 1.125rem;
  color: ${theme.mono40};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

const closeCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: none;
  color: ${theme.mono70};
  cursor: pointer;

  &:hover {
    background: ${theme.dove};
  }
`;

const bodyCss = css`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 1rem;
  padding: 1rem;
`;

/* Paper card treatment shared by the globe and list panes. */
const paperCss = (theme: BirdiTheme) => css`
  background: ${theme.mono0};
  border: 1px solid ${theme.dove};
  border-radius: 5px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const globeCss = (theme: BirdiTheme) => css`
  ${paperCss(theme)};
  flex: 1;
  min-width: 0;
  position: relative;

  /* Hide Cesium chrome we can't disable via props. */
  .cesium-viewer-bottom {
    display: none;
  }
`;

/* Top-centre of the globe pane, above Cesium, while results load. */
const globeLoadingCss = (theme: BirdiTheme) => css`
  position: absolute;
  top: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  padding: 0.35rem 0.6rem;
  border-radius: 5px;
  background: ${theme.mono0};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  pointer-events: none;
`;

const globeFallbackCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

/* The results column: tiles (one per type) bring their own paper. */
const listPaneCss = css`
  width: 26rem;
  max-width: 40vw;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const visuallyHiddenCss = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
